/**
 * 发券申请 API client —— 运营主管提交申请、超管审批后系统才真正发券。
 *
 * 对应 admin-service 顶层 /voucher-grant-requests（不在 /vouchers 下，避免被 /vouchers/:id 吞掉）。
 * 设计：project-docs/specs/2026-10-15-ops-lead-role-design.md（D4 / I8 / §5-C）。
 *
 * approve 三种结果：
 *   200  { status: 'approved', voucherIds }   已发出
 *   409  业务错误（cp 明确拒绝，一张没发；申请已退回「发券失败」，可再次通过）→ axios reject，message 即原因
 *   202  { status: 'unknown' } + message      结果不明：不是成功也不是确定的失败，须到免单券列表核对后在审批页处理
 */
import { apiClient } from './client';

export type VoucherGrantRequestStatus =
  | 'pending'
  | 'approving'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'failed'
  | 'unknown';

/** 展示状态：多一个 expired（pending 超过 7 天）；approving 超过 5 分钟按 unknown */
export type VoucherGrantRequestDisplayStatus = VoucherGrantRequestStatus | 'expired';

export interface VoucherGrantRequestDto {
  id: string;
  /** 落库状态 */
  status: VoucherGrantRequestStatus;
  /** 展示用状态（含计算态） */
  displayStatus: VoucherGrantRequestDisplayStatus;
  applicantId: string;
  applicantNickname: string;
  userCount: number;
  /** 券有效期 epoch-ms */
  expireAt: number;
  reason: string;
  decidedBy: string | null;
  decidedAt: number | null;
  decisionReason: string | null;
  voucherIds: string[] | null;
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;
}

/** 逐用户审批上下文（不含手机号 / unionId） */
export interface ApprovalContextRow {
  userId: string;
  /** false = users 表里已查无此人（申请后被清理） */
  exists: boolean;
  nickname: string | null;
  registeredAt: number | null;
  /** 已付费订单数 */
  paidOrderCount: number;
  /** 当前有效券数 */
  validVoucherCount: number;
  /** 近 30 天已领券数 */
  recentVoucherCount: number;
}

export interface VoucherGrantRequestDetail extends VoucherGrantRequestDto {
  userIds: string[];
  decidedByNickname: string | null;
  context: ApprovalContextRow[];
}

export interface ListVoucherGrantRequestsQuery {
  status?: VoucherGrantRequestDisplayStatus;
  /** 审批人也只看自己提交的 */
  mine?: boolean;
  page?: number;
  pageSize?: number;
}

export interface ListVoucherGrantRequestsResponse {
  items: VoucherGrantRequestDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateVoucherGrantRequestInput {
  userIds: string[];
  /** epoch-ms，须大于当前时间 */
  expireAt: number;
  reason: string;
}

export interface CreateVoucherGrantRequestResponse {
  id: string;
  status: VoucherGrantRequestStatus;
  displayStatus: VoucherGrantRequestDisplayStatus;
  userCount: number;
  expireAt: number;
  createdAt: number;
}

export interface ApproveVoucherGrantRequestResponse {
  id: string;
  status: 'approved' | 'unknown';
  voucherIds?: string[];
  /** 202 结果不明时服务端给的核对提示 */
  message?: string;
}

export type ResolveOutcome = 'granted' | 'not_granted';

export interface PendingCountResponse {
  /** 待审批（pending 且未过期） */
  count: number;
  /** 结果不明（含超时 approving） */
  unknownCount: number;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

const BASE = '/voucher-grant-requests';

export async function listVoucherGrantRequests(
  q: ListVoucherGrantRequestsQuery = {},
): Promise<ListVoucherGrantRequestsResponse> {
  const params: Record<string, string> = {};
  if (q.status) params.status = q.status;
  if (q.mine) params.mine = 'true';
  if (q.page != null) params.page = String(q.page);
  if (q.pageSize != null) params.pageSize = String(q.pageSize);
  const resp = await apiClient.get<ServiceResponse<ListVoucherGrantRequestsResponse>>(BASE, { params });
  return resp.data.data;
}

export async function getVoucherGrantRequest(id: string): Promise<VoucherGrantRequestDetail> {
  const resp = await apiClient.get<ServiceResponse<VoucherGrantRequestDetail>>(
    `${BASE}/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

export async function getVoucherGrantPendingCount(): Promise<PendingCountResponse> {
  const resp = await apiClient.get<ServiceResponse<PendingCountResponse>>(`${BASE}/pending-count`);
  return resp.data.data;
}

export async function createVoucherGrantRequest(
  input: CreateVoucherGrantRequestInput,
): Promise<CreateVoucherGrantRequestResponse> {
  const resp = await apiClient.post<ServiceResponse<CreateVoucherGrantRequestResponse>>(BASE, input);
  return resp.data.data;
}

export async function approveVoucherGrantRequest(id: string): Promise<ApproveVoucherGrantRequestResponse> {
  const resp = await apiClient.post<ServiceResponse<ApproveVoucherGrantRequestResponse>>(
    `${BASE}/${encodeURIComponent(id)}/approve`,
  );
  // 202 结果不明时 message 在响应顶层（data 里只有 id / status）
  return { ...resp.data.data, message: resp.data.message };
}

export async function rejectVoucherGrantRequest(id: string, reason: string): Promise<{ id: string; status: 'rejected' }> {
  const resp = await apiClient.post<ServiceResponse<{ id: string; status: 'rejected' }>>(
    `${BASE}/${encodeURIComponent(id)}/reject`,
    { reason },
  );
  return resp.data.data;
}

export async function cancelVoucherGrantRequest(id: string): Promise<{ id: string; status: 'cancelled' }> {
  const resp = await apiClient.post<ServiceResponse<{ id: string; status: 'cancelled' }>>(
    `${BASE}/${encodeURIComponent(id)}/cancel`,
  );
  return resp.data.data;
}

export async function resolveVoucherGrantRequest(
  id: string,
  outcome: ResolveOutcome,
  note: string,
): Promise<{ id: string; status: 'approved' | 'failed' }> {
  const resp = await apiClient.post<ServiceResponse<{ id: string; status: 'approved' | 'failed' }>>(
    `${BASE}/${encodeURIComponent(id)}/resolve`,
    { outcome, note },
  );
  return resp.data.data;
}
